import { useState, useEffect, useMemo, useCallback } from "react";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../../firebase";
import styles from "../../../styles/AdminConsole.module.css";
import { DB_CATEGORIES, INITIAL_DB_FORM } from "../../../data/constants";
import { useUploadToR2 } from "../shared/hooks/useUploadToR2";
import StatCard from "../shared/components/StatCard";
import SelectWrapper from "../shared/components/SelectWrapper";
import Pagination from "../shared/components/Pagination";
import Modal from "../shared/components/Modal";

const ITEMS_PER_PAGE = 15;

function dbSpecializationLabel(category) {
  const map = {
    Spinning: "Yarn Types (e.g. 30s Cotton, CVC, Polyester)",
    Weaving: "Fabric Blends (e.g. Cotton/Poly, Linen/Viscose)",
    Knitting: "Knit Structures (e.g. Single Jersey, Rib, Interlock)",
    Dyeing: "Dye Infrastructure (e.g. Reactive, Vat, Pigment)",
    Printing: "Print Infrastructure (e.g. Rotary, Digital, Screen)",
    Washing: "Wash Types (e.g. Enzyme, Stone, Acid)",
    Embroidery: "Embroidery Capabilities (e.g. Flat, 3D, Sequin)",
    Trims: "Trim Types (e.g. Buttons, Zippers, Labels, Elastic)",
  };
  return map[category] ?? "Specialization / Notes";
}

export default function DatabaseTab({ showToast }) {
  // ── Own Firebase subscription ─────────────────────────────────────────────
  const [dbRecords, setDbRecords] = useState([]);
  const [loadingDb, setLoadingDb] = useState(true);

  useEffect(() => {
    const q = query(collection(db, "database"), orderBy("name", "asc"));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setDbRecords(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoadingDb(false);
      },
      (err) => {
        console.error("Failed to fetch database:", err);
        setLoadingDb(false);
      },
    );
    return () => unsub();
  }, []);
  const { uploadToR2, deleteFromR2 } = useUploadToR2();
  // ── Table state ───────────────────────────────────────────────────────────
  const [filterCategory, setFilterCategory] = useState("all");
  const [sortDir, setSortDir] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);

  // ── Modal state ───────────────────────────────────────────────────────────
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [productModal, setProductModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [profileUploading, setProfileUploading] = useState(false);
  const [productUploading, setProductUploading] = useState(false);

  const [form, setForm] = useState(INITIAL_DB_FORM);
  const setField = useCallback(
    (k, v) => setForm((p) => ({ ...p, [k]: v })),
    [],
  );

  // ── Derived data ──────────────────────────────────────────────────────────
  const filteredRecords = useMemo(
    () =>
      dbRecords
        .filter(
          (r) => filterCategory === "all" || r.category === filterCategory,
        )
        .sort((a, b) => {
          const na = (a.name || "").toLowerCase();
          const nb = (b.name || "").toLowerCase();
          return sortDir === "asc"
            ? na.localeCompare(nb)
            : nb.localeCompare(na);
        }),
    [dbRecords, filterCategory, sortDir],
  );
  const totalPages = Math.ceil(filteredRecords.length / ITEMS_PER_PAGE);
  const paginatedRecords = useMemo(
    () =>
      filteredRecords.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE,
      ),
    [filteredRecords, currentPage],
  );

  // ── Handlers ──────────────────────────────────────────────────────────────
  const openNew = useCallback(() => {
    setEditTarget(null);
    setForm(INITIAL_DB_FORM);
    setModalOpen(true);
  }, []);
  const openEdit = useCallback((record) => {
    setEditTarget(record);
    setForm({
      name: record.name || "",
      category: record.category || "Spinning",
      country: record.country || "",
      city: record.city || "",
      contact: record.contact || "",
      email: record.email || "",
      certifications: (record.certifications || []).join(", "),
      notes: record.notes || "",
      profilePdf: record.profilePdf || "",
      products: record.products || "",
      specialization: record.specialization || "",
    });
    setModalOpen(true);
  }, []);
  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditTarget(null);
    setForm(INITIAL_DB_FORM);
  }, []);

  const handleSave = useCallback(async () => {
    if (!form.name.trim()) {
      showToast("Name is required.", "error");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        category: form.category,
        country: form.country.trim(),
        city: form.city.trim(),
        contact: form.contact.trim(),
        email: form.email.trim(),
        certifications: form.certifications
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        notes: form.notes.trim(),
        profilePdf: form.profilePdf.trim(),
        products: form.products.trim(),
        specialization: form.specialization.trim(),
        updatedAt: serverTimestamp(),
      };
      if (editTarget) {
        await updateDoc(doc(db, "database", editTarget.id), payload);
        showToast(`"${payload.name}" updated.`);
      } else {
        await addDoc(collection(db, "database"), {
          ...payload,
          createdAt: serverTimestamp(),
        });
        showToast(`"${payload.name}" added to database.`);
      }
      closeModal();
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setSaving(false);
    }
  }, [form, editTarget, showToast, closeModal]);

  const handleDelete = useCallback(
    async (record) => {
      if (!window.confirm(`Delete "${record.name}"? This cannot be undone.`))
        return;
      setDeleting(record.id);
      try {
        await deleteDoc(doc(db, "database", record.id));
        showToast(`"${record.name}" deleted.`, "error");
      } catch (err) {
        showToast(err.message, "error");
      } finally {
        setDeleting(null);
      }
    },
    [showToast],
  );

  const handleProfileUpload = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setProfileUploading(true);
      try {
        const url = await uploadToR2(file, "database/profiles");
        setField("profilePdf", url);
        showToast("Profile PDF uploaded.");
      } catch (err) {
        showToast(err.message, "error");
      } finally {
        setProfileUploading(false);
      }
    },
    [uploadToR2, setField, showToast],
  );

  const handleProductUpload = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setProductUploading(true);
      try {
        const url = await uploadToR2(file, "database/products");
        setField("products", url);
        showToast("Products file uploaded.");
      } catch (err) {
        showToast(err.message, "error");
      } finally {
        setProductUploading(false);
      }
    },
    [uploadToR2, setField, showToast],
  );

  const [removingProfile, setRemovingProfile] = useState(false);
  const [removingProduct, setRemovingProduct] = useState(false);

  const handleRemoveProfile = useCallback(async () => {
    if (!window.confirm("Remove this profile PDF?")) return;
    setRemovingProfile(true);
    try {
      await deleteFromR2(form.profilePdf);
      setField("profilePdf", "");
      if (editTarget) {
        await updateDoc(doc(db, "database", editTarget.id), {
          profilePdf: "",
          updatedAt: serverTimestamp(),
        });
      }
      showToast("Profile PDF removed.");
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setRemovingProfile(false);
    }
  }, [form.profilePdf, editTarget, deleteFromR2, setField, showToast]);

  const handleRemoveProduct = useCallback(async () => {
    if (!window.confirm("Remove this products file?")) return;
    setRemovingProduct(true);
    try {
      await deleteFromR2(form.products);
      setField("products", "");
      if (editTarget) {
        await updateDoc(doc(db, "database", editTarget.id), {
          products: "",
          updatedAt: serverTimestamp(),
        });
      }
      showToast("Products file removed.");
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setRemovingProduct(false);
    }
  }, [form.products, editTarget, deleteFromR2, setField, showToast]);
  return (
    <>
      <h2 className={styles.sectionTitle}>Mill &amp; Supplier Database</h2>

      {/* ── Stats ── */}
      <div className={styles.statsRow}>
        <StatCard
          label="Total Records"
          value={dbRecords.length}
          sub="all database entries"
        />
        {DB_CATEGORIES.slice(0, 5).map((cat) => (
          <StatCard
            key={cat}
            label={cat}
            value={dbRecords.filter((r) => r.category === cat).length}
            sub={`${cat.toLowerCase()} suppliers`}
          />
        ))}
      </div>

      <div className={styles.rule} />

      {/* ── Table ── */}
      <div className={styles.tableWrapper} style={{ maxWidth: "100%" }}>
        <div className={styles.tableControls}>
          <div className={styles.tableFilters}>
            <div className={styles.tableFilterGroup}>
              <span className={styles.tableFilterLabel}>Category</span>
              <SelectWrapper
                value={filterCategory}
                selectClassName={styles.tableFilterSelect}
                onChange={(e) => {
                  setFilterCategory(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="all">All</option>
                {DB_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </SelectWrapper>
            </div>
          </div>
          <button
            className={`${styles.btn} ${styles.btnPrimary}`}
            style={{ marginLeft: "auto" }}
            onClick={openNew}
          >
            + Add Entry
          </button>
        </div>

        {loadingDb ? (
          <p className={styles.stateText}>Loading database…</p>
        ) : (
          <div className={styles.tableScroll}>
            <table className={styles.table}>
              <thead>
                <tr className={styles.tableHeadRow}>
                  <th className={styles.th}>#</th>
                  <th className={styles.th}>
                    <button
                      className={styles.sortBtn}
                      onClick={() => {
                        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
                        setCurrentPage(1);
                      }}
                    >
                      Name{" "}
                      <span className={styles.sortIcon}>
                        {sortDir === "asc" ? "↑" : "↓"}
                      </span>
                    </button>
                  </th>
                  <th className={styles.th}>Category</th>
                  <th className={styles.th}>City</th>
                  <th className={styles.th}>Country</th>
                  <th className={styles.th}>Contact</th>
                  <th className={styles.th}>Specialization</th>
                  <th className={styles.th}>Profile</th>
                  <th className={styles.th}>Products</th>
                  <th className={styles.th}></th>
                </tr>
              </thead>
              <tbody>
                {paginatedRecords.map((record, index) => (
                  <tr key={record.id} className={styles.tableRow}>
                    <td className={styles.td}>
                      {(currentPage - 1) * ITEMS_PER_PAGE + index + 1}
                    </td>
                    <td className={styles.td} style={{ fontWeight: 600 }}>
                      {record.name}
                    </td>
                    <td className={styles.td}>
                      <span
                        className={`${styles.categoryBadge} ${styles.cat_common}`}
                      >
                        {record.category}
                      </span>
                    </td>
                    <td className={styles.td}>{record.city || "—"}</td>
                    <td className={styles.td}>{record.country || "—"}</td>
                    <td className={styles.td}>{record.contact || "—"}</td>
                    <td
                      className={styles.td}
                      style={{
                        maxWidth: 200,
                        fontSize: 11,
                        color: "var(--text-muted)",
                      }}
                    >
                      {record.specialization || "—"}
                    </td>
                    <td className={styles.td}>
                      {record.profilePdf ? (
                        <a
                          href={record.profilePdf}
                          target="_blank"
                          rel="noreferrer"
                          className={styles.supplierLink}
                        >
                          View ↗
                        </a>
                      ) : (
                        <span style={{ opacity: 0.35 }}>—</span>
                      )}
                    </td>
                    <td className={styles.td}>
                      {record.products ? (
                        <button
                          className={styles.supplierLink}
                          onClick={() => setProductModal(record.products)}
                        >
                          Preview
                        </button>
                      ) : (
                        <span style={{ opacity: 0.35 }}>—</span>
                      )}
                    </td>
                    <td className={styles.td}>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button
                          className={styles.editBtn}
                          onClick={() => openEdit(record)}
                          title="Edit"
                        >
                          ✎
                        </button>
                        <button
                          className={styles.editBtn}
                          style={{ color: "#e08080" }}
                          onClick={() => handleDelete(record)}
                          disabled={deleting === record.id}
                          title="Delete"
                        >
                          {deleting === record.id ? "…" : "✕"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      </div>

      <div className={styles.rule} />

      {/* ── Add / Edit Modal ── */}
      {modalOpen && (
        <Modal
          title={
            editTarget ? `Edit — ${editTarget.name}` : "Add Database Entry"
          }
          onClose={closeModal}
          maxWidth={640}
        >
          <div className={styles.formInner}>
            <SelectWrapper
              value={form.category}
              onChange={(e) => setField("category", e.target.value)}
            >
              {DB_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </SelectWrapper>

            <input
              className={styles.input}
              placeholder="Name *"
              value={form.name}
              onChange={(e) => setField("name", e.target.value)}
            />
            <input
              className={styles.input}
              placeholder="Country"
              value={form.country}
              onChange={(e) => setField("country", e.target.value)}
            />
            <input
              className={styles.input}
              placeholder="City"
              value={form.city}
              onChange={(e) => setField("city", e.target.value)}
            />
            <input
              className={styles.input}
              placeholder="Contact Number"
              value={form.contact}
              onChange={(e) => setField("contact", e.target.value)}
            />
            <input
              className={styles.input}
              placeholder="Email"
              type="email"
              value={form.email}
              onChange={(e) => setField("email", e.target.value)}
            />
            <input
              className={styles.input}
              placeholder="Certifications (comma-separated, e.g. GOTS, OEKO-TEX)"
              value={form.certifications}
              onChange={(e) => setField("certifications", e.target.value)}
            />
            <input
              className={styles.input}
              placeholder={dbSpecializationLabel(form.category)}
              value={form.specialization}
              onChange={(e) => setField("specialization", e.target.value)}
            />
            <textarea
              className={styles.input}
              placeholder="Notes"
              rows={3}
              style={{ resize: "vertical", minHeight: 72, flexShrink: 0 }}
              value={form.notes}
              onChange={(e) => setField("notes", e.target.value)}
            />

            {/* Profile PDF */}
            <div className={styles.uploadBlock}>
              <span className={styles.uploadBlockLabel}>Profile PDF</span>
              {form.profilePdf && (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <a
                    href={form.profilePdf}
                    target="_blank"
                    rel="noreferrer"
                    className={styles.uploadCurrentFile}
                  >
                    Current file ↗
                  </a>
                  <button
                    type="button"
                    className={styles.editBtn}
                    style={{ color: "#e08080" }}
                    title="Remove file"
                    disabled={removingProfile}
                    onClick={handleRemoveProfile}
                  >
                    {removingProfile ? "…" : "✕"}
                  </button>
                </div>
              )}
              <div className={styles.fileInputWrapper}>
                <span
                  className={styles.fileInputBtn}
                  style={{
                    pointerEvents: profileUploading ? "none" : "auto",
                    opacity: profileUploading ? 0.4 : 1,
                  }}
                >
                  Choose PDF
                  <input
                    type="file"
                    accept=".pdf"
                    disabled={profileUploading}
                    className={styles.fileInputNative}
                    onChange={handleProfileUpload}
                  />
                </span>
                {profileUploading ? (
                  <span className={styles.uploadStatus}>Uploading…</span>
                ) : (
                  <span className={styles.fileInputName}>
                    {form.profilePdf ? "File selected" : "No file chosen"}
                  </span>
                )}
              </div>
            </div>

            {/* Products */}
            <div className={styles.uploadBlock}>
              <span className={styles.uploadBlockLabel}>
                Products (PDF or Image)
              </span>
              {form.products && (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <button
                    type="button"
                    className={styles.uploadCurrentFile}
                    onClick={() => setProductModal(form.products)}
                  >
                    Preview current file
                  </button>
                  <button
                    type="button"
                    className={styles.editBtn}
                    style={{ color: "#e08080" }}
                    title="Remove file"
                    disabled={removingProduct}
                    onClick={handleRemoveProduct}
                  >
                    {removingProduct ? "…" : "✕"}
                  </button>
                </div>
              )}
              <div className={styles.fileInputWrapper}>
                <span
                  className={styles.fileInputBtn}
                  style={{
                    pointerEvents: productUploading ? "none" : "auto",
                    opacity: productUploading ? 0.4 : 1,
                  }}
                >
                  Choose File
                  <input
                    type="file"
                    accept=".pdf,image/*"
                    disabled={productUploading}
                    className={styles.fileInputNative}
                    onChange={handleProductUpload}
                  />
                </span>
                {productUploading ? (
                  <span className={styles.uploadStatus}>Uploading…</span>
                ) : (
                  <span className={styles.fileInputName}>
                    {form.products ? "File selected" : "No file chosen"}
                  </span>
                )}
              </div>
            </div>

            <button
              className={`${styles.btn} ${styles.btnPrimary}`}
              onClick={handleSave}
              disabled={saving || profileUploading || productUploading}
            >
              {saving ? "Saving…" : editTarget ? "Save Changes" : "Add Entry"}
            </button>
          </div>
        </Modal>
      )}

      {/* ── Product Preview Modal ── */}
      {productModal && (
        <Modal
          title="Product Preview"
          onClose={() => setProductModal(null)}
          maxWidth={800}
          centerContent
          style={{
            width: "90vw",
            height: "80vh",
            padding: 0,
            overflow: "hidden",
          }}
        >
          {productModal.match(/\.(pdf)$/i) ? (
            <iframe
              src={productModal}
              style={{
                width: "100%",
                height: "calc(100% - 48px)",
                border: "none",
              }}
              title="Product PDF"
            />
          ) : (
            <div
              style={{
                width: "100%",
                height: "calc(100% - 48px)",
                overflow: "auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 16,
              }}
            >
              <img
                src={productModal}
                alt="Product"
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                }}
              />
            </div>
          )}
        </Modal>
      )}
    </>
  );
}
